import * as React from "react";
import { useEffect, useState } from "react";
import { Box, Typography, Grid, Button, useMediaQuery, useTheme, Card, CardContent, CardMedia, IconButton, InputAdornment, Switch } from "@mui/material";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { centered_flex_box, main_button, MainInput } from "../../app/components/Styles";
import { returnsSystemSubscribe, updateReturnsSystemCustomization, updateReturnsSystemProducts, getUploadUrl, getProductsSimplified } from "../../app/store/actions/dataActions";
import { refreshPassword } from "../../app/store/actions/authActions";
import NewCard from "../../app/components/NewCard";
import { Table } from "antd";
import { PasswordVerificationModal } from "../../app/components";
import { CloudUpload, CheckCircle, Security, Speed, Support, Palette, Image, Visibility, ArrowBack, ArrowForward, Search, Warning } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const SuscribeFLow = ({ user, refreshPassword, returnsSystemSubscribe, updateReturnsSystemCustomization, updateReturnsSystemProducts, getUploadUrl, getProductsSimplified, products, isLoading }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();

  const [subscribed, setSubscribed] = useState(!!user?.vendor?.hasReturnSystem);
  const [primaryColor, setPrimaryColor] = useState(user?.vendor?.primaryColor || "#F7463C");
  const [secondaryColor, setSecondaryColor] = useState(user?.vendor?.secondaryColor || "#0F2C65");
  const [logoUrl, setLogoUrl] = useState(user?.vendor?.logo || "");
  const [attachmentsRequired, setAttachmentsRequired] = useState(!!user?.vendor?.returnSystemAttachmentsRequired);
  const [exchangeSameSkuOnly, setExchangeSameSkuOnly] = useState(!!user?.vendor?.exchangeSameSkuOnly);
  const [returnPolicy, setReturnPolicy] = useState(user?.vendor?.returnPolicy || "");
  const [returnPolicyDays, setReturnPolicyDays] = useState(user?.vendor?.returnPolicyDays || 30);
  const [productFlags, setProductFlags] = useState([]);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [step, setStep] = useState(subscribed ? 1 : 0); // 0: subscribe, 1: branding, 2: return policy, 3: products
  const [search, setSearch] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);

  useEffect(() => {
    if (subscribed) {
      getProductsSimplified();
    }
  }, [subscribed]);

  // Keep toggle in sync after partner/vendor refresh from customization save
  useEffect(() => {
    setExchangeSameSkuOnly(!!user?.vendor?.exchangeSameSkuOnly);
  }, [user?.vendor?.exchangeSameSkuOnly]);

  useEffect(() => {
    if (products && Array.isArray(products)) {
      const mappedProducts = products.map((p) => ({
        product: p._id,
        name: p.name,
        sku: p.sku || p.externalId,
        isReturnable: p.isReturnable !== undefined ? !!p.isReturnable : true,
        isExchangable: p.isExchangable !== undefined ? !!p.isExchangable : true,
      }));
      setProductFlags(mappedProducts);
      setFilteredProducts(mappedProducts);
    } else if (products?.docs && Array.isArray(products.docs)) {
      const mappedProducts = products.docs.map((p) => ({
        product: p._id,
        name: p.name,
        sku: p.sku || p.externalId,
        isReturnable: p.isReturnable !== undefined ? !!p.isReturnable : true,
        isExchangable: p.isExchangable !== undefined ? !!p.isExchangable : true,
      }));
      setProductFlags(mappedProducts);
      setFilteredProducts(mappedProducts);
    }
  }, [products]);

  const openPasswordModal = () => setPasswordModalOpen(true);

  const handlePasswordVerify = async (password) => {
    try {
      const result = await refreshPassword(password);
      if (result.type === "WAIT_SUCCESS") {
        setPasswordModalOpen(false);
        returnsSystemSubscribe(undefined, () => {});
        setSubscribed(true);
        setStep(1);
      }
    } catch (e) {}
  };

  const handlePasswordModalClose = () => setPasswordModalOpen(false);

  const uploadLogo = async (file) => {
    const fileType = file.type;
    const key = `returns-system-logos/${user?._id}-${Date.now()}-${file.name}`;
    const { uploadURL, publicURL } = await getUploadUrl({ fileType, key });
    await fetch(uploadURL, { method: "PUT", headers: { "Content-Type": fileType }, body: file });
    setLogoUrl(publicURL);
  };

  const handleLogoInput = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadLogo(file);
      e.target.value = '';
    }
  };

  const handleSaveBrandingAndNext = () => {
    updateReturnsSystemCustomization({
      primaryColor,
      secondaryColor,
      logo: logoUrl,
      returnSystemAttachmentsRequired: attachmentsRequired,
      exchangeSameSkuOnly: !!exchangeSameSkuOnly,
    });
    setStep(2);
  };

  const handleSaveReturnPolicyAndNext = () => {
    updateReturnsSystemCustomization({
      primaryColor,
      secondaryColor,
      logo: logoUrl,
      returnSystemAttachmentsRequired: attachmentsRequired,
      exchangeSameSkuOnly: !!exchangeSameSkuOnly,
      returnPolicy,
      returnPolicyDays,
    });
    setStep(3);
  };

  const handleSaveProducts = () => {
    const productUpdates = productFlags.map(({ product, isReturnable, isExchangable }) => ({ product, isReturnable, isExchangable }));
    updateReturnsSystemProducts({ productUpdates });
  };

  const handleSearch = () => {
    if (!search.trim()) {
      setFilteredProducts(productFlags);
      return;
    }
    
    const filtered = productFlags.filter((product) => 
      product.name.toLowerCase().includes(search.toLowerCase()) ||
      product.sku.toLowerCase().includes(search.toLowerCase())
    );
    setFilteredProducts(filtered);
  };

  const handleClearSearch = () => {
    setSearch('');
    setFilteredProducts(productFlags);
  };

  // Table renders filteredProducts; keep both lists in sync when toggling flags
  const toggleProductFlag = (productId, flag) => {
    const apply = (list) =>
      list.map((x) => (x.product === productId ? { ...x, [flag]: !x[flag] } : x));
    setProductFlags(apply);
    setFilteredProducts(apply);
  };

  const flagButtonSx = (active) => ({
    ...main_button,
    bgcolor: active ? 'var(--primaryColor)' : 'var(--secColor)',
    background: active ? 'var(--primaryColor)' : 'var(--secColor)',
    boxShadow: 'none',
    textTransform: 'none',
    py: 0.5,
    px: 1.5,
    minWidth: 56,
    fontSize: '0.75rem',
    ':hover': {
      bgcolor: active ? 'var(--primaryColor)' : 'var(--secColor)',
      background: active ? 'var(--primaryColor)' : 'var(--secColor)',
      opacity: 0.9,
    },
  });

  const columns = [
    { title: t("common.sku"), dataIndex: 'sku', key: 'sku' },
    { title: t("common.name"), dataIndex: 'name', key: 'name' },
    {
      title: t("returnsSystem.manageProducts.exchangeable"), key: 'isExchangable',
      render: (_, record) => (
        <Button
          size="small"
          sx={flagButtonSx(record.isExchangable)}
          onClick={() => toggleProductFlag(record.product, 'isExchangable')}
        >
          {record.isExchangable ? t("common.yes") : t("common.no")}
        </Button>
      )
    },
    {
      title: t("returnsSystem.manageProducts.returnable"), key: 'isReturnable',
      render: (_, record) => (
        <Button
          size="small"
          sx={flagButtonSx(record.isReturnable)}
          onClick={() => toggleProductFlag(record.product, 'isReturnable')}
        >
          {record.isReturnable ? t("common.yes") : t("common.no")}
        </Button>
      )
    },
  ];

  return (
    <Box sx={{ width: "100%", px: isMobile ? 2 : 5, py: isMobile ? 3 : 6 }}>
      <Box sx={{ ...centered_flex_box, flexDirection: "column", textAlign: "center", gap: 2 }}>
        <Typography sx={{ color: "var(--secColor4)", fontWeight: 700 }} fontSize={isMobile ? 22 : 28}>{t("returnsSystem.subscribeFlow.title")}</Typography>
        <Typography sx={{ color: "var(--secColor4)", opacity: 0.8, fontSize: isMobile ? 14 : 16 }}>{t("returnsSystem.home.tagline")}</Typography>
      </Box>

      {/* Step 0 - Subscribe */}
      {step === 0 && (
        <Grid container spacing={3} sx={{ mt: 2 }}>
          {/* Main Subscription Card */}
          <Grid item xs={12} md={8}>
            <NewCard content={
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Box sx={{ mb: 3 }}>
                  <CloudUpload sx={{ fontSize: 64, color: 'var(--primaryColor)', mb: 2 }} />
                  <Typography variant="h4" sx={{ color: "var(--secColor4)", fontWeight: 700, mb: 2 }}>
                    {t("returnsSystem.subscribeFlow.enableTitle")}
                  </Typography>
                  <Typography variant="h6" sx={{ color: "var(--secColor4)", mb: 3, maxWidth: 600, mx: 'auto' }}>
                    {t("returnsSystem.subscribeFlow.enableDescription")}
                  </Typography>
                </Box>

                {/* Features Grid */}
                <Grid container spacing={2} sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ ...centered_flex_box, flexDirection: 'column', gap: 1, p: 2 }}>
                      <Security sx={{ fontSize: 32, color: 'var(--primaryColor)' }} />
                      <Typography variant="subtitle2" fontWeight={600}>{t("returnsSystem.subscribeFlow.features.securePortal.title")}</Typography>
                      <Typography variant="caption" sx={{ color: 'var(--secColor4)', textAlign: 'center' }}>
                        {t("returnsSystem.subscribeFlow.features.securePortal.description")}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ ...centered_flex_box, flexDirection: 'column', gap: 1, p: 2 }}>
                      <Speed sx={{ fontSize: 32, color: 'var(--primaryColor)' }} />
                      <Typography variant="subtitle2" fontWeight={600}>{t("returnsSystem.subscribeFlow.features.fastProcessing.title")}</Typography>
                      <Typography variant="caption" sx={{ color: 'var(--secColor4)', textAlign: 'center' }}>
                        {t("returnsSystem.subscribeFlow.features.fastProcessing.description")}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ ...centered_flex_box, flexDirection: 'column', gap: 1, p: 2 }}>
                      <CheckCircle sx={{ fontSize: 32, color: 'var(--primaryColor)' }} />
                      <Typography variant="subtitle2" fontWeight={600}>{t("returnsSystem.subscribeFlow.features.easySetup.title")}</Typography>
                      <Typography variant="caption" sx={{ color: 'var(--secColor4)', textAlign: 'center' }}>
                        {t("returnsSystem.subscribeFlow.features.easySetup.description")}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ ...centered_flex_box, flexDirection: 'column', gap: 1, p: 2 }}>
                      <Support sx={{ fontSize: 32, color: 'var(--primaryColor)' }} />
                      <Typography variant="subtitle2" fontWeight={600}>{t("returnsSystem.subscribeFlow.features.support.title")}</Typography>
                      <Typography variant="caption" sx={{ color: 'var(--secColor4)', textAlign: 'center' }}>
                        {t("returnsSystem.subscribeFlow.features.support.description")}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                  <Button 
                    sx={{ 
                      ...main_button, 
                      px: 4, 
                      py: 1.5,
                      fontSize: '1.1rem',
                      minWidth: 200
                    }} 
                    onClick={openPasswordModal}
                  >
                    {t("returnsSystem.subscribeFlow.getStarted")}
                  </Button>
                  <Button 
                    sx={{ 
                      ...main_button, 
                      bgcolor: 'var(--secColor)',
                      px: 4, 
                      py: 1.5,
                      fontSize: '1.1rem',
                      minWidth: 200
                    }} 
                    onClick={() => navigate('/returns-system')}
                  >
                    {t("returnsSystem.subscribeFlow.learnMore")}
                  </Button>
                </Box>
              </Box>
            } />
          </Grid>

          {/* Pricing Card */}
          <Grid item xs={12} md={4}>
            <Card sx={{ 
              height: '100%',
              borderRadius: 3,
              border: '2px solid var(--primaryColor)',
              position: 'relative',
              overflow: 'visible'
            }}>
              <Box sx={{
                position: 'absolute',
                top: -12,
                left: '50%',
                transform: 'translateX(-50%)',
                bgcolor: 'var(--primaryColor)',
                color: 'white',
                px: 2,
                py: 0.5,
                borderRadius: 2,
                fontSize: '0.875rem',
                fontWeight: 600
              }}>
                {t("returnsSystem.subscribeFlow.recommended")}
              </Box>
              <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h5" fontWeight={700} sx={{ color: 'var(--secColor4)', mb: 1 }}>
                  {t("returnsSystem.subscribeFlow.planTitle")}
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h3" fontWeight={700} sx={{ color: 'var(--primaryColor)' }}>
                    2,500
                  </Typography>
                  <Typography variant="h6" sx={{ color: 'var(--secColor4)' }}>
                    {t("returnsSystem.subscribeFlow.planPriceSuffix")}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'var(--secColor4)', opacity: 0.8 }}>
                    {t("returnsSystem.subscribeFlow.usageBasedPricing")}
                  </Typography>
                </Box>
                
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'var(--secColor4)', mb: 2 }}>
                    {t("returnsSystem.subscribeFlow.whatsIncluded")}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {[
                      t("returnsSystem.subscribeFlow.included.customizablePortal"),
                      t("returnsSystem.subscribeFlow.included.exchangeManagement"),
                      t("returnsSystem.subscribeFlow.included.productRules"),
                      t("returnsSystem.subscribeFlow.included.customerNotifications"),
                      t("returnsSystem.subscribeFlow.included.analyticsDashboard"),
                      t("returnsSystem.subscribeFlow.included.apiIntegration"),
                      t("returnsSystem.subscribeFlow.included.prioritySupport")
                    ].map((feature, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircle sx={{ fontSize: 16, color: 'var(--primaryColor)' }} />
                        <Typography variant="body2" sx={{ color: 'var(--secColor4)' }}>
                          {feature}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Box sx={{ mt: 3, p: 2, bgcolor: 'var(--warningColor)', borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'white', mb: 1 }}>
                    {t("returnsSystem.subscribeFlow.pricingBreakdownTitle")}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'white', mb: 0.5 }}>
                    {t("returnsSystem.home.pricingStructure.base")}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'white', mb: 0.5 }}>
                    {t("returnsSystem.home.pricingStructure.tier1")}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'white', mb: 1 }}>
                    {t("returnsSystem.home.pricingStructure.tier2")}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'white', textAlign: 'center', fontWeight: 500 }}>
                    {t("returnsSystem.home.pricingStructure.cancelAnytime")}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Step 1 - Branding */}
      {step === 1 && (
        <Grid container spacing={3} sx={{ mt: 2 }}>
          {/* Main Branding Card */}
          <Grid item xs={12} md={8}>
            <NewCard content={
              <Box sx={{ p: 3 }}>
                {/* Header */}
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <Palette sx={{ fontSize: 48, color: 'var(--primaryColor)', mb: 2 }} />
                  <Typography variant="h4" sx={{ color: "var(--secColor4)", fontWeight: 700, mb: 1 }}>
                    {t("returnsSystem.subscribeFlow.customizeTitle")}
                  </Typography>
                  <Typography variant="h6" sx={{ color: "var(--secColor4)", opacity: 0.8 }}>
                    {t("returnsSystem.subscribeFlow.step1Subtitle")}
                  </Typography>
                </Box>

                <Grid container spacing={4}>
                  {/* Logo Upload Section */}
                  <Grid item xs={12}>
                    <Box sx={{ 
                      border: '2px dashed var(--secColor)', 
                      borderRadius: 3, 
                      p: 4, 
                      textAlign: 'center',
                      bgcolor: logoUrl ? 'var(--appBg)' : 'transparent',
                      transition: 'all 0.3s ease'
                    }}>
                      <Image sx={{ fontSize: 48, color: 'var(--primaryColor)', mb: 2 }} />
                      <Typography variant="h6" sx={{ color: 'var(--secColor4)', mb: 1 }}>
                        {t("returnsSystem.subscribeFlow.uploadLogoTitle")}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'var(--secColor4)', opacity: 0.7, mb: 3 }}>
                        {t("returnsSystem.subscribeFlow.uploadLogoHint")}
                      </Typography>
                      
                      {logoUrl ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ 
                            p: 2, 
                            border: '1px solid var(--secColor)', 
                            borderRadius: 2,
                            bgcolor: 'white'
                          }}>
                            <img 
                              src={logoUrl} 
                              alt="logo preview" 
                              style={{ 
                                maxHeight: 80, 
                                maxWidth: 200, 
                                objectFit: 'contain' 
                              }} 
                            />
                          </Box>
                          <Button 
                            component="label" 
                            sx={{ 
                              ...main_button, 
                              bgcolor: 'var(--secColor)',
                              px: 3
                            }}
                          >
                            {t("returnsSystem.subscribeFlow.changeLogo")}
                            <input hidden type="file" accept="image/*" onChange={handleLogoInput} />
                          </Button>
                        </Box>
                      ) : (
                        <Button 
                          component="label" 
                          sx={{ 
                            ...main_button, 
                            px: 4,
                            py: 1.5,
                            fontSize: '1rem'
                          }}
                        >
                          {t("returnsSystem.subscribeFlow.chooseFile")}
                          <input hidden type="file" accept="image/*" onChange={handleLogoInput} />
                        </Button>
                      )}
                    </Box>
                  </Grid>

                  {/* Color Customization */}
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ color: 'var(--secColor4)', fontWeight: 600, mb: 3 }}>
                      {t("returnsSystem.subscribeFlow.brandColors")}
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ 
                          p: 3, 
                          border: '1px solid var(--secColor)', 
                          borderRadius: 2,
                          bgcolor: 'var(--appBg)'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Box sx={{ 
                              width: 20, 
                              height: 20, 
                              borderRadius: '50%', 
                              bgcolor: primaryColor,
                              border: '2px solid white',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }} />
                            <Typography variant="subtitle1" fontWeight={600} sx={{ color: 'var(--secColor4)' }}>
                              {t("profile.primaryColor")}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <input 
                              type="color" 
                              value={primaryColor} 
                              onChange={(e) => setPrimaryColor(e.target.value)} 
                              style={{ 
                                width: 60, 
                                height: 60, 
                                border: 'none', 
                                borderRadius: 12,
                                cursor: 'pointer',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                              }} 
                            />
                            <Box>
                              <Typography variant="body2" sx={{ color: 'var(--secColor4)', fontFamily: 'monospace', fontWeight: 600 }}>
                                {primaryColor.toUpperCase()}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'var(--secColor4)', opacity: 0.7 }}>
                                {t("returnsSystem.subscribeFlow.primaryColorHint")}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <Box sx={{ 
                          p: 3, 
                          border: '1px solid var(--secColor)', 
                          borderRadius: 2,
                          bgcolor: 'var(--appBg)'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Box sx={{ 
                              width: 20, 
                              height: 20, 
                              borderRadius: '50%', 
                              bgcolor: secondaryColor,
                              border: '2px solid white',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }} />
                            <Typography variant="subtitle1" fontWeight={600} sx={{ color: 'var(--secColor4)' }}>
                              {t("profile.secondaryColor")}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <input 
                              type="color" 
                              value={secondaryColor} 
                              onChange={(e) => setSecondaryColor(e.target.value)} 
                              style={{ 
                                width: 60, 
                                height: 60, 
                                border: 'none', 
                                borderRadius: 12,
                                cursor: 'pointer',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                              }} 
                            />
                            <Box>
                              <Typography variant="body2" sx={{ color: 'var(--secColor4)', fontFamily: 'monospace', fontWeight: 600 }}>
                                {secondaryColor.toUpperCase()}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'var(--secColor4)', opacity: 0.7 }}>
                                {t("returnsSystem.subscribeFlow.secondaryColorHint")}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                  <Typography variant="h6" sx={{ color: 'var(--secColor4)', fontWeight: 600, mb: 2 }}>
                    {t("returnsSystem.subscribeFlow.attachmentsTitle")}
                  </Typography>
                  <Box sx={{ 
                    p: 3, 
                    border: '1px solid var(--secColor)', 
                    borderRadius: 2,
                    bgcolor: 'var(--appBg)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2
                  }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ color: 'var(--secColor4)' }}>
                        {t("returnsSystem.subscribeFlow.requireImageAttachments")}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'var(--secColor4)', opacity: 0.7 }}>
                        {t("returnsSystem.subscribeFlow.requireImageAttachmentsHint")}
                      </Typography>
                    </Box>
                    <Switch
                      checked={attachmentsRequired}
                      onChange={(e) => setAttachmentsRequired(e.target.checked)}
                      inputProps={{ 'aria-label': t("returnsSystem.subscribeFlow.attachmentsToggleAria") }}
                    />
                  </Box>
                  </Grid>
                  <Grid item xs={12}>
                  <Typography variant="h6" sx={{ color: 'var(--secColor4)', fontWeight: 600, mb: 2 }}>
                    {t("returnsSystem.subscribeFlow.exchangeSettingsTitle")}
                  </Typography>
                  <Box sx={{ 
                    p: 3, 
                    border: '1px solid var(--secColor)', 
                    borderRadius: 2,
                    bgcolor: 'var(--appBg)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2
                  }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ color: 'var(--secColor4)' }}>
                        {t("returnsSystem.subscribeFlow.exchangeSameSkuOnly")}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'var(--secColor4)', opacity: 0.7 }}>
                        {t("returnsSystem.subscribeFlow.exchangeSameSkuOnlyHint")}
                      </Typography>
                    </Box>
                    <Switch
                      checked={exchangeSameSkuOnly}
                      onChange={(e) => setExchangeSameSkuOnly(e.target.checked)}
                      inputProps={{ 'aria-label': t("returnsSystem.subscribeFlow.exchangeSameSkuOnlyToggleAria") }}
                    />
                  </Box>
                  </Grid>
                </Grid>
              </Box>
            } />
          </Grid>

          {/* Preview Card */}
          <Grid item xs={12} md={4}>
            <Card sx={{ 
              height: '100%',
              borderRadius: 3,
              border: '1px solid var(--secColor)',
              bgcolor: 'var(--appBg)'
            }}>
              <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <Visibility sx={{ fontSize: 20, color: 'var(--primaryColor)' }} />
                  <Typography variant="h6" fontWeight={600} sx={{ color: 'var(--secColor4)' }}>
                    {t("returnsSystem.subscribeFlow.livePreview")}
                  </Typography>
                </Box>
                
                {/* Preview Content */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Logo Preview */}
                  <Box sx={{ 
                    p: 2, 
                    border: '1px solid var(--secColor)', 
                    borderRadius: 2,
                    bgcolor: 'white',
                    textAlign: 'center'
                  }}>
                    {logoUrl ? (
                      <img 
                        src={logoUrl} 
                        alt="logo preview" 
                        style={{ 
                          maxHeight: 40, 
                          maxWidth: 120, 
                          objectFit: 'contain' 
                        }} 
                      />
                    ) : (
                      <Typography variant="body2" sx={{ color: 'var(--secColor4)', opacity: 0.5 }}>
                        {t("returnsSystem.subscribeFlow.logoWillAppearHere")}
                      </Typography>
                    )}
                  </Box>

                  {/* Color Preview */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Box sx={{ 
                      flex: 1, 
                      height: 40, 
                      bgcolor: primaryColor, 
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Typography variant="caption" sx={{ 
                        color: 'white', 
                        fontWeight: 600,
                        textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                      }}>
                        {t("returnsSystem.subscribeFlow.primaryLabel")}
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      flex: 1, 
                      height: 40, 
                      bgcolor: secondaryColor, 
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Typography variant="caption" sx={{ 
                        color: 'white', 
                        fontWeight: 600,
                        textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                      }}>
                        {t("returnsSystem.subscribeFlow.secondaryLabel")}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Sample Button */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Button sx={{ 
                      bgcolor: primaryColor,
                      color: 'white',
                      px: 3,
                      py: 1,
                      borderRadius: 2,
                      fontWeight: 600,
                      '&:hover': {
                        bgcolor: secondaryColor
                      }
                    }}>
                      {t("returnsSystem.subscribeFlow.sampleButton")}
                    </Button>
                  </Box>
                </Box>

                {/* Action Buttons */}
                <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Button 
                    sx={{ 
                      ...main_button, 
                      width: '100%',
                      py: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }} 
                    onClick={() => setStep(0)}
                  >
                    <ArrowBack sx={{ fontSize: 18 }} />
                    {t("common.back")}
                  </Button>
                  <Button 
                    sx={{ 
                      ...main_button, 
                      width: '100%',
                      py: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }} 
                    onClick={handleSaveBrandingAndNext}
                  >
                    {t("returnsSystem.subscribeFlow.saveAndContinue")}
                    <ArrowForward sx={{ fontSize: 18 }} />
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Step 2 - Return Policy */}
      {step === 2 && (
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} md={8}>
            <NewCard content={
              <Box sx={{ p: 3 }}>
                {/* Header */}
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <Typography variant="h4" sx={{ color: "var(--secColor4)", fontWeight: 700, mb: 1 }}>
                    {t("returnsSystem.subscribeFlow.returnPolicyTitle")}
                  </Typography>
                  <Typography variant="h6" sx={{ color: "var(--secColor4)", opacity: 0.8 }}>
                    {t("returnsSystem.subscribeFlow.step2Subtitle")}
                  </Typography>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Box sx={{ 
                      p: 3, 
                      border: '1px solid var(--secColor)', 
                      borderRadius: 2,
                      bgcolor: 'var(--appBg)'
                    }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ color: 'var(--secColor4)', mb: 1 }}>
                        {t("returnsSystem.subscribeFlow.returnPolicyText")}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'var(--secColor4)', opacity: 0.7, mb: 2 }}>
                        {t("returnsSystem.subscribeFlow.returnPolicyTextHint")}
                      </Typography>
                      <MainInput
                        margin="normal"
                        fullWidth
                        multiline
                        rows={4}
                        value={returnPolicy}
                        onChange={(e) => setReturnPolicy(e.target.value)}
                        placeholder={t("returnsSystem.subscribeFlow.returnPolicyPlaceholder")}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ 
                      p: 3, 
                      border: '1px solid var(--secColor)', 
                      borderRadius: 2,
                      bgcolor: 'var(--appBg)'
                    }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ color: 'var(--secColor4)', mb: 1 }}>
                        {t("returnsSystem.subscribeFlow.returnPolicyDays")}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'var(--secColor4)', opacity: 0.7, mb: 2 }}>
                        {t("returnsSystem.subscribeFlow.returnPolicyDaysHint")}
                      </Typography>
                      <MainInput
                        margin="normal"
                        fullWidth
                        type="number"
                        value={returnPolicyDays}
                        onChange={(e) => setReturnPolicyDays(parseInt(e.target.value) || 0)}
                        inputProps={{ min: 0 }}
                      />
                      {returnPolicyDays > 0 && returnPolicyDays < 14 && (
                        <Box sx={{ 
                          mt: 2, 
                          p: 2, 
                          bgcolor: 'var(--warningColor)', 
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5
                        }}>
                          <Warning sx={{ color: 'white', fontSize: 20, mt: 0.25, flexShrink: 0 }} />
                          <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                            {t("returnsSystem.subscribeFlow.returnPolicyDaysWarning")}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            } />
          </Grid>

          {/* Navigation Card */}
          <Grid item xs={12} md={4}>
            <Card sx={{ 
              height: '100%',
              borderRadius: 3,
              border: '1px solid var(--secColor)',
              bgcolor: 'var(--appBg)'
            }}>
              <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Button 
                    sx={{ 
                      ...main_button, 
                      width: '100%',
                      py: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }} 
                    onClick={() => setStep(1)}
                  >
                    <ArrowBack sx={{ fontSize: 18 }} />
                    {t("common.back")}
                  </Button>
                  <Button 
                    sx={{ 
                      ...main_button, 
                      width: '100%',
                      py: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }} 
                    onClick={handleSaveReturnPolicyAndNext}
                  >
                    {t("returnsSystem.subscribeFlow.saveAndContinue")}
                    <ArrowForward sx={{ fontSize: 18 }} />
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Step 3 - Products */}
      {step === 3 && (
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <NewCard content={
              <Box>
                <Typography sx={{ color: "var(--secColor4)", fontWeight: 600, mb: 2 }}>{t("returnsSystem.subscribeFlow.step3Title")}</Typography>
                
                {/* Search Bar */}
                <Box sx={{ mb: 3 }}>
                  <MainInput
                    margin="normal"
                    placeholder={t("returnsSystem.manageProducts.searchPlaceholder")}
                    value={search}
                    onChange={({ target }) => setSearch(target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch();
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {search && (
                            <IconButton
                              onClick={handleClearSearch}
                              edge="end"
                              sx={{ color: "var(--secColor4)" }}
                            >
                              ×
                            </IconButton>
                          )}
                          <IconButton
                            onClick={handleSearch}
                            edge="end"
                            sx={{ color: "var(--secColor4)" }}
                          >
                            <Search />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ width: '100%', maxWidth: 400 }}
                  />
                </Box>
                
                <Table
                  columns={columns}
                  dataSource={filteredProducts}
                  loading={isLoading}
                  rowKey={(r) => r.product}
                  pagination={{ pageSize: 10 }}
                  scroll={{ x: isMobile ? 300 : undefined }}
                />
                <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  <Button sx={{ ...main_button }} onClick={() => setStep(2)}>{t("common.back")}</Button>
                  <Button sx={{ ...main_button }} onClick={handleSaveProducts}>{t("common.save")}</Button>
                </Box>
              </Box>
            } />
          </Grid>
        </Grid>
      )}

      <PasswordVerificationModal
        open={passwordModalOpen}
        onClose={handlePasswordModalClose}
        onVerify={handlePasswordVerify}
        isLoading={isLoading}
        title={t("returnsSystem.subscribeFlow.verifyPasswordTitle")}
        message={t("returnsSystem.subscribeFlow.verifyPasswordMessage")}
      />
    </Box>
  );
};

const mapStateToProps = (state) => ({
  user: state?.auth?.user,
  products: state?.records?.products,
  isLoading: state?.records?.isLoading || state?.wait?.isLoading,
});

const mapDispatchToProps = {
  refreshPassword,
  returnsSystemSubscribe,
  updateReturnsSystemCustomization,
  updateReturnsSystemProducts,
  getUploadUrl,
  getProductsSimplified,
};

export default connect(mapStateToProps, mapDispatchToProps)(SuscribeFLow);

